import {
  BriefcaseBusiness,
  CircleAlert,
  CreditCard,
  ExternalLink,
  UserRound,
} from 'lucide-react'
import { useContext } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { adminEmployees, adminEmployers, adminMembers, brokersMembers } from 'rails_routes'
import { Badge, Banner } from 'slab'
import { useGetBrokersMembersMemberId } from 'utils/web_api'
import { type BrokersBrokerMemberDenormalized } from 'utils/web_api.schemas'

import EmployerReviewStatusBadge from './EmployerReviewStatusBadge'
import EmploymentStatusBadge from './EmploymentStatusBadge'
import UnitApprovalStatusBadge from './UnitApprovalStatusBadge'

const BrokersMembersSummaryInfoSkeleton = () => {
  return (
    <div className='mb-4 flex items-center gap-2'>
      {/* Employment Status Badge Skeleton */}
      <div className='inline-flex items-center rounded-lg py-1 animate-pulse'>
        <div className='h-4 w-16 bg-gray-300 rounded' />
      </div>

      {/* Employer Review Status Badge Skeleton */}
      <div className='inline-flex items-center rounded-lg py-1 animate-pulse'>
        <div className='h-4 w-20 bg-gray-300 rounded' />
      </div>

      {/* Unit Application Status Badge Skeleton */}
      <div className='inline-flex items-center rounded-lg py-1 animate-pulse'>
        <div className='h-4 w-20 bg-gray-300 rounded' />
      </div>
    </div>
  )
}

type BrokersMembersSummaryInfoProps = {
  memberId: string
  employerId: string
}

const AdminNavigationBadges = ({
  memberId,
  employerId,
}: BrokersMembersSummaryInfoProps) => {
  const { isUserThatchAdmin } = useContext(BrokersMemberContext)

  if (!isUserThatchAdmin) {
    return null
  }

  return (
    <>
      <Badge
        type='info'
        leftIcon={UserRound}
        actionIcon={ExternalLink}
        onActionClick={() => {
          window.open(adminMembers.show({ id: memberId }), '_blank')
        }}
      >
        Admin View
      </Badge>
      <Badge
        type='info'
        leftIcon={BriefcaseBusiness}
        actionIcon={ExternalLink}
        onActionClick={() => {
          window.open(adminEmployers.show({ id: employerId }), '_blank')
        }}
      >
        Employer Admin View
      </Badge>
    </>
  )
}

const CobraCoverageBadge = ({
  member,
}: {
  member: BrokersBrokerMemberDenormalized
}) => {
  const {
    termination_config: terminationConfig,
    employee_id: employeeId,
  } = member

  const defaultBadge = <Badge type='neutral'>Not eligible for COBRA</Badge>
  if (!terminationConfig) {
    return defaultBadge
  }

  const {
    cobra_eligible: cobraEligible,
    post_termination_coverage: postTerminationCoverage,
  } = terminationConfig

  // if a termination config exists, we know the member definitely has an employee id
  const badgeLinkProps = {
    actionIcon: ExternalLink,
    onActionClick: () => {
      window.open(adminEmployees.edit({ id: employeeId }))
    },
  }

  if (cobraEligible) {
    switch (postTerminationCoverage) {
      case 'cobra':
        return <Badge type='success' {...badgeLinkProps}>🐍 Accepted COBRA coverage</Badge>
      case 'decline':
        return <Badge type='critical' {...badgeLinkProps}>🐍 Declined COBRA coverage</Badge>
      case 'unselected':
        return <Badge type='caution' {...badgeLinkProps}>🐍 COBRA eligible but no selection made</Badge>
    }
  }
  return defaultBadge
}

const TransactionsNavigationBadge = ({
  memberId,
}: Omit<BrokersMembersSummaryInfoProps, 'employerId'>) => {
  return (
    <Badge
      type='info'
      leftIcon={CreditCard}
      actionIcon={ExternalLink}
      onActionClick={() => {
        window.open(brokersMembers.transactions({ member_id: memberId }), '_blank')
      }}
    >
      Transactions
    </Badge>
  )
}

const VipEmployerBadge = ({
  isVipEmployer,
}: {
  isVipEmployer: boolean
}) => {
  if (!isVipEmployer) {
    return null
  }

  return (
    <Badge type='slate' leftIcon={CircleAlert}>
      VIP Employer
    </Badge>
  )
}

const BrokersMembersSummaryInfo = ({
  memberId,
  employerId,
}: BrokersMembersSummaryInfoProps) => {
  const {
    data: member,
    isLoading,
    error,
  } = useGetBrokersMembersMemberId(memberId, {
    query: {
      enabled: !!memberId,
      refetchOnWindowFocus: false,
    },
  })

  if (isLoading) {
    return <BrokersMembersSummaryInfoSkeleton />
  }

  if (error) {
    return (
      <Banner title='Error loading member' type='critical'>
        {(error as GrapeAPIErrorType)?.message ||
          'An error occurred while loading the member.'}
      </Banner>
    )
  }

  if (!member) {
    return null
  }

  const {
    employment_status: employmentStatus,
    vip_employer: vipEmployer,
  } = member
  return (
    <div className='mb-4 flex items-center gap-2 flex-wrap flex-shrink-0'>
      <VipEmployerBadge isVipEmployer={vipEmployer || false} />

      <EmploymentStatusBadge member={member} />

      <EmployerReviewStatusBadge member={member} />

      <UnitApprovalStatusBadge member={member} />

      <TransactionsNavigationBadge memberId={memberId} />

      <AdminNavigationBadges
        memberId={memberId}
        employerId={employerId}
      />
      {employmentStatus === 'inactive' && <CobraCoverageBadge member={member} />}
    </div>
  )
}

export default BrokersMembersSummaryInfo
